import { useMemo } from 'react';
import removeOtherTraffic from 'src/components/BeaconV2Components/utils/removeOtherTraffic';
import useAdvancedSearchData, { AdvancedSearchParsedDatum } from 'src/serverProxy/useAdvancedSearchData';
import { useAppSelector } from 'src/utils/Hooks';
import useGetAdvancedSearchFieldKey from 'src/utils/Hooks/useGetAdvancedSearchFieldKey';

export const useTrafficDataWithDPV = () => {
  const getAdvancedSearchFieldKey = useGetAdvancedSearchFieldKey();
  const entity = useAppSelector((state) => state?.entityService?.mainEntity);

  const { data: dpvData, isLoading: isDpvLoading } = useAdvancedSearchData({
    fields: [{ name: 'dpv' }],
    groupByField: 'retailerId',
    indexName: 'advertisingDisplay',
    requestId: `trafficByKeywordWithDPV-advertisingDisplay-dpv-${entity?.type}-${entity?.id}`
  });

  const { data: targetData, isLoading: isTargetLoading } = useAdvancedSearchData({
    fields: [{ name: 'totalClicks' }],
    groupByField: 'searchTerm',
    indexName: 'traffic',
    requestId: `trafficByKeywordWithDPV-traffic-totalClicks-${entity?.type}-${entity?.id}`,
    requestBuilder: (builder) => {
      const build = builder.apply(removeOtherTraffic);
      build.addConditionTermFilter('searchTerm', 'must_not', ['[DSP Traffic]']).modifyAggregation((agg) => {
        return agg.addConditionTermFilter('searchTerm', ['[DSP Traffic]'], 'must_not');
      });
      return build;
    }
  });

  const parsedData = useMemo(() => {
    const comparisonData = [];
    const currentData = [];
    const categoryData = [];

    if (!isDpvLoading && !isTargetLoading) {
      const key = getAdvancedSearchFieldKey('advertisingDisplay', 'dpv');
      const currentDpv = (dpvData[0] as AdvancedSearchParsedDatum)?.values?.[key];
      const compareDpv = (dpvData[0] as AdvancedSearchParsedDatum)?.comparisonValues?.[key];

      currentData.push(['[DSP Traffic]', currentDpv]);
      comparisonData.push(['[DSP Traffic]', compareDpv]);
      categoryData.push({ name: '[DSP Traffic]', id: '[DSP Traffic]', type: 'searchTerm' });

      targetData.forEach((datum) => {
        const categoryName = datum?.metadata?.name ?? datum?.comparisonMetadata?.name;
        const categoryId = datum?.fieldId;
        const formattedCategoryObj = {
          name: categoryName,
          id: categoryId,
          type: 'searchTerm'
        };

        const responseKey = getAdvancedSearchFieldKey('traffic', 'totalClicks');
        const currentDatum = datum?.values?.[responseKey] ?? 0;
        const comparisonDatum = datum?.comparisonValues?.[responseKey] ?? 0;

        comparisonData.push([categoryName, comparisonDatum]);
        currentData.push([categoryName, currentDatum]);
        categoryData.push(formattedCategoryObj);
      });
    }

    return { currentData, categoryData, comparisonData };
  }, [isDpvLoading, isTargetLoading, dpvData, targetData]);

  return {
    currentData: parsedData.currentData,
    categoryData: parsedData.categoryData,
    comparisonData: parsedData.comparisonData,
    isDpvLoading,
    isTargetLoading
  };
}
